import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const PonyclubContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.ponyclub};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: 700;
  margin: 0;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const InfoContent = styled.div`
  h3 {
    font-size: 2rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 1.5rem;
  }
  
  .highlight-box {
    background: white;
    padding: 2rem;
    border-radius: 15px;
    margin: 2rem 0;
    box-shadow: ${props => props.theme.shadows.sm};
    
    h4 {
      color: ${props => props.theme.colors.primary};
      margin-bottom: 1rem;
    }
    
    ul {
      list-style: none;
      padding: 0;
      
      li {
        padding: 0.5rem 0;
        
        &::before {
          content: '🎠';
          margin-right: 0.5rem;
        }
      }
    }
  }
`;

const InfoSidebar = styled.div`
  .info-card {
    background: white;
    padding: 2rem;
    border-radius: 20px;
    box-shadow: ${props => props.theme.shadows.card};
    margin-bottom: 2rem;
    
    h4 {
      color: ${props => props.theme.colors.primary};
      margin-bottom: 1rem;
      font-size: 1.3rem;
    }
    
    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid ${props => props.theme.colors.lightGray};
      
      &:last-child {
        border-bottom: none;
      }
      
      .label {
        color: ${props => props.theme.colors.textLight};
      }
      
      .value {
        font-weight: 600;
        color: ${props => props.theme.colors.text};
      }
    }
  }
`;

const PricingSection = styled.section`
  padding: 4rem 0;
  background: white;
`;

const BookingButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  text-align: center;
  transition: all 0.3s ease;
  margin-top: 2rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(78, 205, 196, 0.4);
  }
`;

const Ponyclub = () => {

  return (
    <PonyclubContainer>
      <Helmet>
        <title>Ponyclub - Der Ponystall | Für Kinder ab 4 Jahren</title>
        <meta name="description" content="Ponyclub für Kinder ab 4 Jahren - spielerischer Umgang mit Ponys, erste Reiterfahrungen und wichtige Sozialkompetenzen in kleinen Gruppen." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            Ponyclub
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <InfoGrid>
            <InfoContent>
              <h3>Spielerisches Lernen mit Ponys</h3>
              <p>
                Der Ponyclub ist perfekt für Kinder ab 4 Jahren, die ihre ersten Erfahrungen 
                mit Ponys sammeln möchten. In kleinen Gruppen von maximal 8 Kindern lernen 
                sie spielerisch den respektvollen Umgang mit den Tieren.
              </p>
              <p>
                Jede Stunde ist ein kleines Abenteuer: Die Kinder putzen ihre Ponys, 
                lernen die Grundlagen der Pferdepflege und machen ihre ersten Reiterfahrungen. 
                Dabei steht die Freude am Umgang mit dem Tier immer im Vordergrund.
              </p>

              <div className="highlight-box">
                <h4>Das lernen die Kinder bei uns:</h4>
                <ul>
                  <li>Respektvoller Umgang mit Tieren</li>
                  <li>Grundlagen der Pferdepflege (Putzen, Hufe auskratzen)</li>
                  <li>Erste Reiterfahrungen und Balance</li>
                  <li>Verantwortung übernehmen</li>
                  <li>Soziale Kompetenzen in der Gruppe</li>
                  <li>Selbstvertrauen und Mut entwickeln</li>
                  <li>Konzentration und Geduld</li>
                </ul>
              </div>
            </InfoContent>

            <InfoSidebar>
              <div className="info-card">
                <h4>Wichtige Informationen</h4>
                <div className="info-item">
                  <span className="label">Alter:</span>
                  <span className="value">Ab 4 Jahren</span>
                </div>
                                 <div className="info-item">
                   <span className="label">Gruppengröße:</span>
                   <span className="value">Max. 8 Kinder</span>
                 </div>
                 <div className="info-item">
                   <span className="label">Dauer:</span>
                   <span className="value">60 Minuten</span>
                 </div>
              </div>

              <div className="info-card">
                <h4>Was mitbringen?</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  <li>👕 Bequeme Kleidung</li>
                  <li>👟 Feste Schuhe</li>
                  <li>🧥 Wetterfeste Jacke</li>
                  <li>😊 Gute Laune!</li>
                </ul>
              </div>
            </InfoSidebar>
          </InfoGrid>
        </Container>
      </ContentSection>

      <PricingSection>
        <Container>
          <div className="text-center">
            <h2 className="section-title">Ponyclub buchen</h2>
            <p className="section-subtitle">
              Alle aktuellen Kursangebote und Buchungsmöglichkeiten finden Sie auf unserer Kursübersicht.
            </p>
          </div>

          <div className="text-center" style={{ marginTop: '3rem' }}>
            <BookingButton to="/kurse">
              Zu den Buchungsoptionen
            </BookingButton>
          </div>
        </Container>
      </PricingSection>
    </PonyclubContainer>
  );
};

export default Ponyclub;